/* Copyright (c) 2020-present CloudIO Inc */

import { AnyItem, Page } from '../../registry/ComponentRegistry';
import { ActionCopyFiltersItem } from 'componentregistry/actions/ActionCopyFilters/ActionCopyFilters';
import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionCopyFilterAttribute';

const ActionCopyFilterAttributeManager: ActionManagerFn<
  'ActionCopyFilterAttribute'
> = (platform: Platform): ActionManager<'ActionCopyFilterAttribute'> => {
  return {
    ...baseActionManager(platform, 'ActionCopyFilterAttribute'),
    defaultValue,
    isAllowedInsideParent: (page: Page, parent: AnyItem) => {
      return parent.type === 'ActionCopyFilters';
    },
    props: {
      ...baseActionProps,
      copyFromAttribute: {
        label: 'Copy From Attribute',
        type: 'ViewAttributeChooser',
        required: true,
        getAttributeDataTypes: () => 'ALL',
        aliasType: 'STORE',
        getAlias: (page, item, { pageId }) => {
          return {
            alias: (page[item.parent] as ActionCopyFiltersItem).props.copyFrom,
            embeddedPageId: pageId,
          };
        },
      },
      copyToAttribute: {
        label: 'Copy to Attribute',
        type: 'ViewAttributeChooser',
        required: true,
        getAttributeDataTypes: () => 'ALL',
        aliasType: 'STORE',
        getAlias: (page, item, { pageId }) => {
          return {
            alias: (page[item.parent] as ActionCopyFiltersItem).props.copyTo,
            embeddedPageId: pageId,
          };
        },
      },
    },
    name: 'Copy Filter Attribute',
    getDisplayText: (item) =>
      `${item.props.copyFromAttribute} to ${item.props.copyToAttribute}}`,
    icon: { iconName: 'copy' },
    description: `**Overview**<br/>
The Copy Filter Attribute component is a dedicated configuration unit designed to function strictly as a child of the ActionCopyFilters component. Its primary purpose is to define a specific field-level rule for transferring filter state from one attribute to another.<br/><br/>
This component serves as the mapping logic within a filter replication workflow. It does not function independently; instead, it relies on its parent container to define the source and destination Data Stores, while it focuses solely on selecting the specific attributes within those stores to link together.

**Key Features**<br/>
* **Parent-Restricted Usage:** Enforces strict structural hierarchy via isAllowedInsideParent, preventing usage anywhere except inside an ActionCopyFilters action.
* **Context-Aware Attribute Selection:** Both property pickers (copyFromAttribute and copyToAttribute) dynamically retrieve their options by referencing the copyFrom and copyTo Data Store aliases configured on the parent component.
* **Registry Readability:** The getDisplayText method formats the component's label to show the mapping (Source Attribute to Target Attribute), providing a clear summary in the action list.

**Properties**<br/>
* **Copy From Attribute (Required):** A ViewAttributeChooser property that selects the source field whose filter state will be cloned.
  * *Context:* Derives its data source context from the parent's copyFrom property.
* **Copy to Attribute (Required):** A ViewAttributeChooser property that selects the destination field where the filter will be applied.
  * *Context:* Derives its data source context from the parent's copyTo property.

**Use Cases**<br/>
* **Granular Filter Replication:** Defining precise rules for copying filters, such as mapping a "Status" filter from a summary widget to a "CurrentState" column in a detailed grid.
* **Cross-Store Synchronization:** Linking attributes with different names but similar data (e.g., mapping employee_id to empId) during a filter transfer operation.`,
  };
};

export default ActionCopyFilterAttributeManager;
